/**
 * Title, description, canonical e Open Graph por rota.
 *
 * O React 19 HOISTA `<title>`, `<meta>` e `<link>` renderizados em qualquer
 * lugar da árvore para dentro do `<head>` — então não é preciso react-helmet
 * nem nenhuma outra biblioteca.
 *
 * RESSALVA IMPORTANTE: isso só vale para quem executa JavaScript. Crawlers de
 * WhatsApp/Facebook/Telegram NÃO executam, e continuariam vendo as tags
 * estáticas do `index.html`. Quem resolve isso é o desvio do nginx para
 * `/api/og/produtos/:slug` (fase 18). Estas tags servem ao Google (que renderiza)
 * e à aba do navegador.
 */
import { useEffect } from 'react'

/**
 * Remove as meta tags ESTÁTICAS do `index.html` na primeira montagem.
 *
 * O React 19 hoista `<meta>`/`<link>` para o `<head>`, mas **acrescenta em vez
 * de substituir** — o resultado eram duas `<link rel="canonical">` em toda
 * página de produto, a primeira apontando para a home. Canonical duplicado e
 * contraditório é um defeito de SEO de verdade, não cosmético.
 *
 * As tags do `index.html` continuam existindo porque são o ÚNICO que um
 * crawler sem JavaScript enxerga (WhatsApp, Facebook). Quem executa JS não
 * precisa delas: este efeito as apaga e deixa só as da rota atual.
 */
function useRemoverMetaEstatica(): void {
  useEffect(() => {
    for (const elemento of document.querySelectorAll('[data-meta-estatica]')) {
      elemento.remove()
    }
  }, [])
}

interface PropsMeta {
  titulo: string
  descricao?: string
  /** Caminho absoluto a partir da raiz, ex.: `/bicicletas/caloi-aspen`. */
  caminhoCanonico?: string
  /** URL completa de imagem para a prévia de link. */
  imagem?: string
  tipo?: 'website' | 'product'
  /** Páginas de resultado filtrado não devem ser indexadas. */
  naoIndexar?: boolean
}

const SITE = 'Bicicletas Peniel'

export function Meta({
  titulo,
  descricao,
  caminhoCanonico,
  imagem,
  tipo = 'website',
  naoIndexar = false,
}: PropsMeta) {
  useRemoverMetaEstatica()

  const tituloCompleto = titulo.includes(SITE) ? titulo : `${titulo} | ${SITE}`
  const urlCanonica = caminhoCanonico ? `${window.location.origin}${caminhoCanonico}` : undefined

  return (
    <>
      <title>{tituloCompleto}</title>
      {descricao && <meta name="description" content={descricao} />}
      {urlCanonica && <link rel="canonical" href={urlCanonica} />}
      {naoIndexar && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={tipo} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:title" content={tituloCompleto} />
      {descricao && <meta property="og:description" content={descricao} />}
      {urlCanonica && <meta property="og:url" content={urlCanonica} />}
      {imagem && <meta property="og:image" content={imagem} />}
      <meta name="twitter:card" content="summary_large_image" />
    </>
  )
}
